import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAthletes } from '../services/api';
import Flag from './ui/Flag';
import PageLayout from './ui/PageLayout';

function AthleteAvatar({ name, size = 48 }) {
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map(p => p[0])
    .join('')
    .toUpperCase();
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  const hue = Math.abs(hash) % 360;
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: `hsl(${hue}, 35%, 22%)`,
      border: `2px solid hsl(${hue}, 45%, 32%)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
      fontSize: size * 0.34, fontWeight: 700, color: `hsl(${hue}, 65%, 72%)`,
      letterSpacing: -0.5,
    }}>
      {initials}
    </div>
  );
}

function AthletesPage() {
  const navigate = useNavigate();
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('matches');
  const [sortDir, setSortDir] = useState('desc');
  const [viewMode, setViewMode] = useState('list');
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    getAthletes()
      .then(data => setAthletes(Array.isArray(data) ? data : []))
      .catch(() => setAthletes([]))
      .finally(() => setLoading(false));
  }, []);

  const allCategories = [...new Set(
    athletes.flatMap(a => a.weightCategories || [])
  )].sort((a, b) => {
    const numA = parseInt(a.match(/\d+/)?.[0] || '9999');
    const numB = parseInt(b.match(/\d+/)?.[0] || '9999');
    return numA - numB;
  });

  const filtered = athletes.filter(a => {
    const matchesSearch = !search || (() => {
      const s = search.toLowerCase();
      return (
        a.name.toLowerCase().includes(s) ||
        (a.nationality || '').toLowerCase().includes(s) ||
        (a.events || []).some(e => e.toLowerCase().includes(s)) ||
        (a.weightCategories || []).some(w => w.toLowerCase().includes(s))
      );
    })();
    const matchesCategory = categoryFilter === 'all' ||
      (a.weightCategories || []).includes(categoryFilter);
    return matchesSearch && matchesCategory;
  });

  const sorted = [...filtered].sort((a, b) => {
    let valA, valB;
    switch (sortBy) {
      case 'name':
        valA = a.name.toLowerCase(); valB = b.name.toLowerCase();
        return sortDir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      case 'wins':
        valA = a.wins; valB = b.wins; break;
      case 'winRate':
        valA = a.matches > 0 ? a.wins / a.matches : 0;
        valB = b.matches > 0 ? b.wins / b.matches : 0; break;
      case 'ptScored':
        valA = a.ptScored; valB = b.ptScored; break;
      default:
        valA = a.matches; valB = b.matches;
    }
    return sortDir === 'asc' ? valA - valB : valB - valA;
  });

  // Group athletes by primary weight category (for "all" filter)
  const groupedList = (categoryFilter === 'all' && allCategories.length > 0)
    ? (() => {
        const groups = {};
        const noCategory = [];
        sorted.forEach(a => {
          const cats = a.weightCategories || [];
          if (cats.length === 0) {
            noCategory.push(a);
          } else {
            const primary = cats[0];
            if (!groups[primary]) groups[primary] = [];
            groups[primary].push(a);
          }
        });
        const result = allCategories
          .filter(c => groups[c])
          .map(c => ({ category: c, athletes: groups[c] }));
        if (noCategory.length > 0) result.push({ category: null, athletes: noCategory });
        return result;
      })()
    : [{ category: null, athletes: sorted }];

  function toggleSort(col) {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
  }

  function SortHeader({ col, label, style }) {
    const active = sortBy === col;
    return (
      <span
        style={{ cursor: 'pointer', userSelect: 'none', ...style, color: active ? 'var(--accent)' : 'var(--color-text-dim)' }}
        onClick={() => toggleSort(col)}
      >
        {label}{active ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
      </span>
    );
  }

  const totalMatches = Math.round(athletes.reduce((s, a) => s + a.matches, 0) / 2);
  const totalNations = new Set(athletes.map(a => a.nationality).filter(Boolean)).size;

  const header = (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/')}>
          ← Dashboard
        </button>
        <span style={{ fontWeight: 700, fontSize: 15, letterSpacing: -0.3 }}>Database Atleti</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ display: 'flex', background: 'var(--bg-elevated)', borderRadius: 6, border: '1px solid var(--color-border)', overflow: 'hidden' }}>
          {[
            { mode: 'list', label: '☰' },
            { mode: 'cards', label: '⊞' },
          ].map(({ mode, label }) => (
            <button
              key={mode}
              title={mode === 'list' ? 'Lista' : 'Card'}
              style={{
                background: viewMode === mode ? 'var(--accent)' : 'transparent',
                border: 'none',
                color: viewMode === mode ? '#fff' : 'var(--color-text-dim)',
                padding: '5px 12px', cursor: 'pointer', fontSize: 14,
                transition: 'background 0.15s',
              }}
              onClick={() => setViewMode(mode)}
            >
              {label}
            </button>
          ))}
        </div>
        <div style={{ fontSize: 12, color: 'var(--color-text-dim)' }}>
          {loading ? '…' : `${athletes.length} atleti`}
        </div>
      </div>
    </>
  );

  return (
    <PageLayout header={header}>

        {/* Search */}
        <div style={{ marginBottom: 16 }}>
          <input
            style={{
              width: '100%', maxWidth: 400, background: 'var(--bg-elevated)',
              border: '1px solid var(--color-border)', borderRadius: 8,
              padding: '10px 14px', color: 'var(--color-text)', fontSize: 13, outline: 'none',
            }}
            placeholder="Cerca per nome, nazione, evento, categoria…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {/* Category tabs */}
        {!loading && allCategories.length > 0 && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
            {['all', ...allCategories].map(cat => {
              const count = cat === 'all'
                ? athletes.length
                : athletes.filter(a => (a.weightCategories || []).includes(cat)).length;
              return (
                <button
                  key={cat}
                  style={{
                    background: categoryFilter === cat ? 'var(--accent)' : 'var(--bg-elevated)',
                    border: `1px solid ${categoryFilter === cat ? 'var(--accent)' : 'var(--color-border)'}`,
                    color: categoryFilter === cat ? '#fff' : 'var(--color-text-dim)',
                    borderRadius: 20, padding: '5px 14px', fontSize: 11, fontWeight: 600,
                    cursor: 'pointer', transition: 'all 0.15s', letterSpacing: 0.3,
                  }}
                  onClick={() => setCategoryFilter(cat)}
                >
                  {cat === 'all' ? 'Tutte' : cat}
                  <span style={{ marginLeft: 6, opacity: 0.65, fontWeight: 400 }}>{count}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Summary cards */}
        {!loading && (
          <div className="ds-stat-row">
            {[
              { label: 'Atleti totali', value: athletes.length },
              { label: 'Incontri totali', value: totalMatches },
              { label: 'Nazioni', value: totalNations },
            ].map(({ label, value }) => (
              <div key={label} className="ds-card" style={{ padding: '16px 20px', flex: 1 }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--color-text)', letterSpacing: -1 }}>{value}</div>
                <div style={{ fontSize: 11, color: 'var(--color-text-dim)', marginTop: 3 }}>{label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="ds-card ds-empty-state">Caricamento…</div>
        ) : sorted.length === 0 ? (
          <div className="ds-card ds-empty-state">
            {search ? 'Nessun atleta trovato.' : 'Nessun atleta nel database. Analizza il tuo primo incontro.'}
          </div>
        ) : viewMode === 'cards' ? (

          /* ── CARD VIEW ─────────────────────────────────────── */
          <div>
            {groupedList.map(({ category, athletes: group }) => (
              <div key={category || '__none'} style={{ marginBottom: 28 }}>
                {category && (
                  <div className="ds-section-label" style={{ marginBottom: 12 }}>
                    {category} <span style={{ opacity: 0.5, fontWeight: 400 }}>({group.length})</span>
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 12 }}>
                  {group.map(a => {
                    const winRate = a.matches > 0 ? Math.round((a.wins / a.matches) * 100) : 0;
                    const ptPerMatch = a.matches > 0 ? (a.ptScored / a.matches).toFixed(1) : '—';
                    const winColor = winRate >= 60 ? 'var(--color-green)' : winRate >= 40 ? 'var(--color-orange)' : 'var(--color-red)';
                    return (
                      <div
                        key={a.name}
                        style={{
                          background: 'var(--bg-card)', border: '1px solid var(--color-border)',
                          borderRadius: 'var(--radius-lg)', padding: '18px 16px',
                          cursor: 'pointer', transition: 'border-color 0.15s, transform 0.15s',
                          display: 'flex', flexDirection: 'column', gap: 12,
                        }}
                        onClick={() => navigate(`/atleti/${encodeURIComponent(a.name)}`)}
                        onMouseEnter={e => {
                          e.currentTarget.style.borderColor = 'var(--accent)';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.borderColor = 'var(--color-border)';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        {/* Avatar + name */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <AthleteAvatar name={a.name} size={48} />
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {a.name}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--color-text-dim)', marginTop: 3, display: 'flex', alignItems: 'center' }}>
                              <Flag nat={a.nationality} />
                              {a.nationality || '—'}
                            </div>
                          </div>
                        </div>

                        {/* Weight category badges */}
                        {(a.weightCategories || []).length > 0 && (
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {(a.weightCategories || []).map(w => (
                              <span key={w} style={{
                                fontSize: 9, background: 'var(--bg-elevated)',
                                border: '1px solid var(--color-border)', borderRadius: 3,
                                padding: '2px 6px', color: 'var(--color-text-dim)',
                                fontWeight: 600, letterSpacing: 0.3,
                              }}>
                                {w}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Stats */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4, paddingTop: 10, borderTop: '1px solid var(--color-border)' }}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--color-text)', letterSpacing: -0.5 }}>{a.matches}</div>
                            <div style={{ fontSize: 9, color: 'var(--color-text-dim)', marginTop: 1, letterSpacing: 0.5 }}>INC</div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: -0.5, color: a.wins + a.losses > 0 ? winColor : 'var(--color-text-dim)' }}>
                              {a.wins + a.losses > 0 ? `${winRate}%` : '—'}
                            </div>
                            <div style={{ fontSize: 9, color: 'var(--color-text-dim)', marginTop: 1, letterSpacing: 0.5 }}>VITT</div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--color-blue)', letterSpacing: -0.5 }}>{ptPerMatch}</div>
                            <div style={{ fontSize: 9, color: 'var(--color-text-dim)', marginTop: 1, letterSpacing: 0.5 }}>PT/INC</div>
                          </div>
                        </div>

                        {/* Win rate bar */}
                        {a.matches > 0 && (
                          <div style={{ height: 3, borderRadius: 2, background: 'var(--bg-elevated)', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${winRate}%`, background: winColor, borderRadius: 2, transition: 'width 0.4s' }} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

        ) : (

          /* ── LIST VIEW ─────────────────────────────────────── */
          <div>
            {groupedList.map(({ category, athletes: group }, gi) => (
              <div key={category || '__none'} style={{ marginBottom: gi < groupedList.length - 1 ? 24 : 0 }}>
                {category && (
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-dim)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8, paddingLeft: 2 }}>
                    {category} <span style={{ opacity: 0.5, fontWeight: 400 }}>({group.length})</span>
                  </div>
                )}
                <div className="ds-card" style={{ overflow: 'hidden' }}>
                  <div style={{
                    display: 'grid', gridTemplateColumns: '2fr 60px 70px 70px 80px 80px 80px',
                    padding: '10px 16px', borderBottom: '1px solid var(--color-border)',
                    fontSize: 10, fontWeight: 700, letterSpacing: 0.8,
                  }}>
                    <SortHeader col="name" label="ATLETA" />
                    <SortHeader col="matches" label="INC" style={{ textAlign: 'center' }} />
                    <SortHeader col="wins" label="VINTI" style={{ textAlign: 'center' }} />
                    <span style={{ textAlign: 'center', color: 'var(--color-text-dim)', fontSize: 10, fontWeight: 700 }}>PERSI</span>
                    <SortHeader col="winRate" label="% VITT" style={{ textAlign: 'center' }} />
                    <SortHeader col="ptScored" label="PT FATTI" style={{ textAlign: 'center' }} />
                    <span style={{ textAlign: 'center', color: 'var(--color-text-dim)', fontSize: 10, fontWeight: 700 }}>PT SUBITI</span>
                  </div>

                  {group.map((a, i) => {
                    const winRate = a.matches > 0 ? Math.round((a.wins / a.matches) * 100) : 0;
                    return (
                      <div
                        key={a.name}
                        style={{
                          display: 'grid', gridTemplateColumns: '2fr 60px 70px 70px 80px 80px 80px',
                          padding: '10px 16px',
                          borderBottom: i < group.length - 1 ? '1px solid var(--color-border)' : 'none',
                          alignItems: 'center', cursor: 'pointer', transition: 'background 0.15s',
                        }}
                        onClick={() => navigate(`/atleti/${encodeURIComponent(a.name)}`)}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <AthleteAvatar name={a.name} size={32} />
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600 }}>{a.name}</div>
                            <div style={{ fontSize: 10, color: 'var(--color-text-dim)', marginTop: 1, display: 'flex', alignItems: 'center' }}>
                              <Flag nat={a.nationality} />
                              {a.nationality || ''}
                            </div>
                          </div>
                        </div>
                        <span style={{ textAlign: 'center', fontSize: 13 }}>{a.matches}</span>
                        <span style={{ textAlign: 'center', fontSize: 13, color: 'var(--color-green)', fontWeight: 600 }}>{a.wins}</span>
                        <span style={{ textAlign: 'center', fontSize: 13, color: 'var(--color-red)' }}>{a.losses}</span>
                        <span style={{
                          textAlign: 'center', fontSize: 13, fontWeight: 600,
                          color: winRate >= 60 ? 'var(--color-green)' : winRate >= 40 ? 'var(--color-orange)' : 'var(--color-red)',
                        }}>
                          {a.wins + a.losses > 0 ? `${winRate}%` : '—'}
                        </span>
                        <span style={{ textAlign: 'center', fontSize: 13, color: 'var(--color-blue)' }}>{a.ptScored || '—'}</span>
                        <span style={{ textAlign: 'center', fontSize: 13, color: 'var(--color-text-dim)' }}>{a.ptConceded || '—'}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
    </PageLayout>
  );
}

export default AthletesPage;
